import { BRANDS } from "@/enums/brands";
import { SupportedCountries } from "@/enums/internationalization";
import { THEME } from "@/enums/profile";
import { Assistant } from "@/types/ai";
import { LangString } from "@/types/api";
import { ChatDetails, GptDetails } from "@/types/chat";
import { api, apiFetch } from "./common/api";
import { BASE_GAIL } from "./common/baseUrls";

const OrgsToDetails: Record<
  string,
  {
    brand: BRANDS;
    theme: THEME;
    localization: string;
    welcomeMessage: LangString;
  }
> = {
  "5517131f-1600-423d-bd96-3b45da6c9686": {
    brand: BRANDS.MOVISTAR,
    theme: THEME.LIGHT,
    localization: "es-CO",
    welcomeMessage: {
      en: "Welcome to Movistar Colombia. How can I assist you?",
      es: "Bienvenido a Movistar Colombia. ¿Cómo puedo ayudarte?",
    },
  },
  "854b31da-9467-42e1-a6a4-2945babeebe9": {
    brand: BRANDS.MOVISTAR,
    theme: THEME.LIGHT,
    localization: "es-CO",
    welcomeMessage: {
      en: `
# CONTACT US
Welcome to the Movistar PQR filing module. We will guide you through the process of filing your request, complaint, or claim. Select your product to register your PQR quickly and easily.

> __Select the service for which you want to file:__
> - __Mobile__
> - __Home__ (basic line, internet, and/or television)
> - __Formerly Metrotel or Telebucaramanga__
> - __I am not a user__


Select the option that corresponds to your service to continue with the filing process.
## __Select your product to start the process__

@@@2;2
[$$i:mobile-screen;t:Mobile;d:Prepaid or Postpaid Services;a:I would like to register a PQR for my Mobile service.](https://nowhere.com)
[$$i:house-signal;t:Home;d:Basic line, internet and/or television;a:I would like to register a PQR for my Home service.](https://nowhere.com)
[$$i:house;t:Formerly Metrotel / Telebucaramanga;d:Basic line, internet and/or television;a:I would like to register a PQR for my Formerly Metrotel / Telebucaramanga service.](https://nowhere.com)
[$$i:circle-user;t:Not a User;d:I don't have Movistar products;a:I would like to register a PQR for my Not a User service.](https://nowhere.com)

@@@`,
      es: `
# CONTÁCTANOS
Bienvenido al módulo de radicación de PQRS de Movistar. Te guiaremos en el proceso de radicación de tu solicitud, queja o reclamo. Selecciona tu producto para registrar tu PQRS de forma rápida y sencilla.
> __Selecciona el servicio para el cual deseas radicar:__
> - __Móvil__
> - __Hogar__ (línea básica, internet y/o televisión)
> - __Antes Metrotel o Telebucaramanga__
> - __No soy usuario__


Selecciona la opción que corresponde a tu servicio para continuar con el proceso de radicación.
## __Selecciona tu producto para iniciar el proceso__

@@@2;2
[$$i:mobile-screen;t:Móvil;d:Servicios Prepago o Postpago;a:Quiero registrar un PQRS para mi servicio Móvil.](https://nowhere.com)
[$$i:house-signal;t:Hogar;d:Línea básica, internet y/o televisión;a:Quiero registrar un PQRS para mi servicio Hogar.](https://nowhere.com)
[$$i:house;t:Antes Metrotel / Telebucaramanga;d:Línea básica, internet y/o televisión;a:Quiero registrar un PQRS para mi servicio Antes Metrotel / Telebucaramanga.](https://nowhere.com)
[$$i:circle-user;t:No soy usuario;d:No tengo productos Movistar;a:Quiero registrar un PQRS para mi servicio No soy usuario.](https://nowhere.com)

@@@`,
    },
  },
};

const getOrgDetails = () => {
  const path = window.location.pathname;
  const pathParts = path.split("/");
  const orgId = pathParts.length > 2 ? pathParts[pathParts.length - 2] : null;

  if (orgId && OrgsToDetails[orgId]) {
    return OrgsToDetails[orgId];
  }

  return null;
};

export const GetChatDetails = (): ChatDetails | null => {
  if (!window) {
    return null;
  }

  const location = window.location.href;

  if (!location || !location.startsWith(BASE_GAIL)) {
    return null;
  }

  //Split the path and get the orgId and agentId
  const splitLocation = location.split("/");

  if (splitLocation.length < 2) {
    return null;
  }

  const orgId = splitLocation[splitLocation.length - 2];
  const agentId = splitLocation[splitLocation.length - 1];

  const details = getOrgDetails();

  if (!details) {
    return null;
  }

  return {
    orgId,
    agentId,
    theme: details.theme || THEME.LIGHT,
    brand: details.brand || BRANDS.GAIL,
    localization: details.localization || window.navigator?.language || "en-US",
    countries: [SupportedCountries.UnitedStates],
    welcomeMessage: details.welcomeMessage,
    fileTypes: ["PDF"],
    maxFiles: 1,
  };
};

export const GetAssistantChatChannel = async (): Promise<GptDetails | null> => {
  if (!window) {
    return null;
  }

  const location = window.location.href;

  if (!location || !location.startsWith(BASE_GAIL)) {
    return null;
  }

  const splitLocation = location.split("?")[0].split("/");

  if (splitLocation.length < 2) {
    return null;
  }

  const orgId = splitLocation[splitLocation.length - 2];
  const agentId = splitLocation[splitLocation.length - 1];

  const details = await apiFetch<void, GptDetails>(
    `${BASE_GAIL}/api/public/gpt`,
    "GET",
    {
      headers: {
        "X-Org-ID": orgId,
        "X-Agent-ID": agentId,
      },
    }
  );

  return details;
};
